/**
 * TyJolt Privacy Wallet Service
 *
 * Manages dev wallet keypairs, balances, shielding, and transactions
 * against the TyJolt ZK-private TVM chain. Replaces TonConnect when
 * in TyJolt network mode.
 *
 * Architecture:
 *   Browser (keypair in localStorage)
 *     → TyJolt Producer (/tx, /account)
 *     → Proving Cell (Jolt ZK proofs)
 *     → Validator (verification)
 *
 * Privacy features (NovaBlindFold):
 *   - Shield: move funds from public → shielded pool (Pedersen commitment)
 *   - Unshield: move funds from shielded → public (nullifier proof)
 *   - Shielded transfer: send without revealing amount or recipient
 */

import { getNetworkConfig, TYJOLT_DEV_ACCOUNTS } from '../config/contracts';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TyJoltWallet {
  address: string;
  publicKey: string;
  createdAt: number;
  label: string;
}

export interface WalletBalances {
  public: number;      // RAE in public account
  shielded: number;    // RAE in shielded pool
  total: number;
  publicUsd: string;
  shieldedUsd: string;
  totalUsd: string;
}

export interface TxReceipt {
  txId: string;
  success: boolean;
  gasUsed: number;
  blockHeight: number;
  finality: string;
  preStateRoot: string;
  postStateRoot: string;
  proofPending: boolean;
}

export interface WalletTransaction {
  id: string;
  type: 'public' | 'shielded' | 'shield' | 'unshield' | 'trade';
  hash: string;
  proofHash?: string;
  from?: string;
  to?: string;
  amount?: string;
  amountHidden?: boolean;
  timestamp: number;
  status: 'confirmed' | 'pending' | 'proving' | 'verified';
  blockHeight?: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const WALLET_STORAGE_KEY = 'whether-tyjolt-wallet';
const TX_HISTORY_KEY = 'whether-tyjolt-txs';
const FAUCET_AMOUNT = 100; // RAE per faucet drip (server-controlled)
const RAE_USD_RATE = 1.0; // Testnet: 1 RAE = $1 (placeholder)

// ─── Dev Wallet Accounts ─────────────────────────────────────────────────────
// TyJolt genesis accounts — pre-funded for testing
const DEV_WALLETS: TyJoltWallet[] = [
  { address: '0xAAAA', publicKey: 'dev-pk-aaaa', createdAt: 0, label: 'Trader Alpha' },
  { address: '0xBBBB', publicKey: 'dev-pk-bbbb', createdAt: 0, label: 'Trader Beta' },
  { address: '0xCCCC', publicKey: 'dev-pk-cccc', createdAt: 0, label: 'Trader Gamma' },
  { address: '0xDDDD', publicKey: 'dev-pk-dddd', createdAt: 0, label: 'Trader Delta' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProducerUrl(): string {
  const config = getNetworkConfig();
  return config.producerUrl ?? config.apiBase;
}

function getFaucetUrl(): string | null {
  const config = getNetworkConfig();
  return config.faucetUrl ?? null;
}

function formatRae(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
  return amount.toFixed(2);
}

function raeToUsd(rae: number): string {
  const usd = rae * RAE_USD_RATE;
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Wallet Management ───────────────────────────────────────────────────────

/**
 * Load wallet from localStorage, or create + fund a new one.
 * Returns the active wallet and whether it was just created.
 */
export async function loadOrCreateWallet(): Promise<{ wallet: TyJoltWallet; isNew: boolean }> {
  const existing = loadWallet();
  if (existing) {
    return { wallet: existing, isNew: false };
  }

  // Create a new wallet by assigning a dev account
  const wallet = createWallet();
  saveWallet(wallet);

  // Auto-fund via faucet
  try {
    await requestFaucet(wallet.address);
  } catch (e) {
    console.warn('Faucet request failed:', e);
  }

  return { wallet, isNew: true };
}

export function loadWallet(): TyJoltWallet | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(WALLET_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* invalid JSON */ }
  return null;
}

export function saveWallet(wallet: TyJoltWallet): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(wallet));
}

export function createWallet(): TyJoltWallet {
  // Pick a random dev account that hasn't been claimed
  const existingAddr = loadWallet()?.address;
  const available = DEV_WALLETS.filter(w => w.address !== existingAddr);
  const selected = available[Math.floor(Math.random() * available.length)] ?? DEV_WALLETS[0];

  return {
    ...selected,
    createdAt: Date.now(),
  };
}

export function clearWallet(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(WALLET_STORAGE_KEY);
  localStorage.removeItem(TX_HISTORY_KEY);
}

export function getWalletAddress(): string | null {
  return loadWallet()?.address ?? null;
}

export function shortenAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// ─── Balance Queries ─────────────────────────────────────────────────────────

export async function getBalances(address?: string): Promise<WalletBalances> {
  const addr = address ?? getWalletAddress();
  if (!addr) return emptyBalances();

  try {
    const producerUrl = getProducerUrl();
    const res = await fetch(`${producerUrl}/account/${addr}`);
    if (!res.ok) return emptyBalances();

    const data = await res.json() as { balance: number; nonce: number };
    const publicBal = data.balance ?? 0;

    // Shielded balance would come from the privacy contract
    // For now, track locally from shield/unshield transactions
    const shielded = getLocalShieldedBalance();

    return {
      public: publicBal,
      shielded,
      total: publicBal + shielded,
      publicUsd: raeToUsd(publicBal),
      shieldedUsd: raeToUsd(shielded),
      totalUsd: raeToUsd(publicBal + shielded),
    };
  } catch {
    return emptyBalances();
  }
}

function emptyBalances(): WalletBalances {
  return { public: 0, shielded: 0, total: 0, publicUsd: '$0.00', shieldedUsd: '$0.00', totalUsd: '$0.00' };
}

function getLocalShieldedBalance(): number {
  try {
    const stored = localStorage.getItem('whether-tyjolt-shielded');
    return stored ? parseFloat(stored) : 0;
  } catch { return 0; }
}

function setLocalShieldedBalance(amount: number): void {
  localStorage.setItem('whether-tyjolt-shielded', amount.toString());
}

// ─── Transactions ────────────────────────────────────────────────────────────

/**
 * Submit a transaction to TyJolt Producer.
 */
export async function submitTx(
  to: string,
  value: number = 0,
  data: string = '0x',
  gasLimit: number = 100000,
): Promise<TxReceipt> {
  const from = getWalletAddress();
  if (!from) throw new Error('No wallet connected');

  const producerUrl = getProducerUrl();
  const res = await fetch(`${producerUrl}/tx`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      from,
      to,
      value,
      data,
      gas_limit: gasLimit,
      gas_price: 1,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(`Transaction failed: ${(err as any).error ?? res.statusText}`);
  }

  const result = await res.json() as any;
  const receipt: TxReceipt = {
    txId: result.tx_id ?? '',
    success: result.receipt?.execution_result?.success ?? false,
    gasUsed: result.receipt?.execution_result?.gas_used ?? 0,
    blockHeight: result.receipt?.block_height ?? 0,
    finality: result.receipt?.finality ?? 'pending',
    preStateRoot: result.receipt?.pre_state_root ?? '',
    postStateRoot: result.receipt?.post_state_root ?? '',
    proofPending: true,
  };

  // Store in local history
  addTxToHistory({
    id: receipt.txId,
    type: value > 0 ? 'public' : 'trade',
    hash: receipt.txId.slice(0, 12) + '...',
    from,
    to,
    amount: value > 0 ? formatRae(value) : undefined,
    timestamp: Date.now(),
    status: receipt.success ? 'confirmed' : 'pending',
    blockHeight: receipt.blockHeight,
  });

  return receipt;
}

/**
 * Send a public transfer.
 */
export async function sendPublic(to: string, amount: number): Promise<TxReceipt> {
  return submitTx(to, amount);
}

/**
 * Shield funds — move from public to shielded pool.
 * Generates a Pedersen commitment and submits to the privacy contract.
 */
export async function shieldFunds(amount: number): Promise<TxReceipt> {
  // In production: generate Pedersen commitment, create shield proof
  // For testnet: simulate by transferring to a "shielded" tracking address
  const receipt = await submitTx('0xSHIELD', amount, '0xSHIELD');

  if (receipt.success) {
    const current = getLocalShieldedBalance();
    setLocalShieldedBalance(current + amount);

    addTxToHistory({
      id: receipt.txId,
      type: 'shield',
      hash: receipt.txId.slice(0, 12) + '...',
      from: getWalletAddress() ?? '',
      amount: formatRae(amount),
      timestamp: Date.now(),
      status: 'confirmed',
      blockHeight: receipt.blockHeight,
      proofHash: `ped_cm_${receipt.txId.slice(0, 16)}`,
    });
  }

  return receipt;
}

/**
 * Unshield funds — move from shielded pool to public.
 * Generates a nullifier proof.
 */
export async function unshieldFunds(amount: number): Promise<TxReceipt> {
  const shielded = getLocalShieldedBalance();
  if (amount > shielded) throw new Error(`Insufficient shielded balance: ${shielded} RAE`);

  const receipt = await submitTx('0xUNSHIELD', 0, `0xUNSHIELD_${amount}`);

  if (receipt.success) {
    setLocalShieldedBalance(shielded - amount);

    addTxToHistory({
      id: receipt.txId,
      type: 'unshield',
      hash: receipt.txId.slice(0, 12) + '...',
      to: getWalletAddress() ?? '',
      amount: formatRae(amount),
      timestamp: Date.now(),
      status: 'confirmed',
      blockHeight: receipt.blockHeight,
      proofHash: `null_${receipt.txId.slice(0, 16)}`,
    });
  }

  return receipt;
}

/**
 * Send a shielded transfer (amount and recipient hidden).
 */
export async function sendShielded(to: string, amount: number): Promise<TxReceipt> {
  const shielded = getLocalShieldedBalance();
  if (amount > shielded) throw new Error(`Insufficient shielded balance: ${shielded} RAE`);

  const receipt = await submitTx(to, 0, `0xSHIELDED_${amount}`);

  if (receipt.success) {
    setLocalShieldedBalance(shielded - amount);

    addTxToHistory({
      id: receipt.txId,
      type: 'shielded',
      hash: receipt.txId.slice(0, 12) + '...',
      amountHidden: true,
      timestamp: Date.now(),
      status: 'confirmed',
      blockHeight: receipt.blockHeight,
      proofHash: `nova_bf_${receipt.txId.slice(0, 16)}`,
    });
  }

  return receipt;
}

// ─── Faucet ──────────────────────────────────────────────────────────────────

export async function requestFaucet(address?: string): Promise<boolean> {
  const faucetUrl = getFaucetUrl();
  if (!faucetUrl) return false;

  const target = address ?? getWalletAddress();
  if (!target) return false;

  try {
    const res = await fetch(`${faucetUrl}/drip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: target }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Transaction History ─────────────────────────────────────────────────────

export function getTxHistory(): WalletTransaction[] {
  try {
    const stored = localStorage.getItem(TX_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function addTxToHistory(tx: WalletTransaction): void {
  const history = getTxHistory();
  history.unshift(tx);
  // Keep last 100 transactions
  if (history.length > 100) history.length = 100;
  localStorage.setItem(TX_HISTORY_KEY, JSON.stringify(history));
}

export function clearTxHistory(): void {
  localStorage.removeItem(TX_HISTORY_KEY);
}

// ─── Proof Status Polling ────────────────────────────────────────────────────

export async function getProofStatus(txHash: string): Promise<'pending' | 'proving' | 'verified' | 'failed'> {
  try {
    const producerUrl = getProducerUrl();
    const res = await fetch(`${producerUrl}/receipt/${txHash}`);
    if (!res.ok) return 'pending';

    const data = await res.json() as any;
    if (data.verified) return 'verified';
    if (data.proven) return 'verified'; // proved = verified in dev mode
    if (data.proving) return 'proving';
    return 'pending';
  } catch {
    return 'pending';
  }
}

/**
 * Poll proof status until verified or timeout.
 */
export async function waitForProof(
  txHash: string,
  timeoutMs: number = 30000,
  onProgress?: (status: string) => void,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const status = await getProofStatus(txHash);
    onProgress?.(status);

    if (status === 'verified') return true;
    if (status === 'failed') return false;

    await new Promise(r => setTimeout(r, 1000));
  }

  return false; // timeout
}

// ─── Network Status ──────────────────────────────────────────────────────────

export async function getNetworkStatus(): Promise<{
  online: boolean;
  blockHeight: number;
  proofHeight: number;
  proofGap: number;
  devMode: boolean;
  provingMode: string;
} | null> {
  try {
    const producerUrl = getProducerUrl();
    const res = await fetch(`${producerUrl}/status`);
    if (!res.ok) return null;

    const data = await res.json() as any;
    return {
      online: true,
      blockHeight: data.block_height ?? 0,
      proofHeight: data.proof_height ?? 0,
      proofGap: data.proof_gap ?? 0,
      devMode: data.dev_mode ?? false,
      provingMode: data.proving_mode ?? 'unknown',
    };
  } catch {
    return null;
  }
}

// ─── Utility Exports ─────────────────────────────────────────────────────────

export { formatRae, raeToUsd, DEV_WALLETS };
